import React, { useState, useMemo } from 'react';
import {
    Box, Card, CardContent, Typography, Alert, Stack,
    ToggleButton, ToggleButtonGroup, Popover,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    useTheme, Avatar
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';

const AttendanceStats = ({ stats }) => {
    const theme = useTheme();
    const [filters, setFilters] = useState(['Training', 'Spiel', 'Event']);
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [popoverContent, setPopoverContent] = useState(null);
    const EVENT_CELL_WIDTH = 80;
    const PLAYER_COLUMN_WIDTH = 120;

    const variantConfig = {
        zusage: { icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />, color: "green", label: 'Zugesagt' },
        absage: { icon: <CancelRoundedIcon sx={{ fontSize: 18 }} />, color: theme.palette.error.main, label: 'Abgesagt' },
        vielleicht: { icon: <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />, color: theme.palette.warning.main, label: 'Vielleicht' },
        null: { icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />, color: theme.palette.text.disabled, label: 'Offen' },
    };

    const handleFilterChange = (event, newFilters) => {
        if (newFilters.length > 0) {
            setFilters(newFilters);
        }
    };

    const calculateParticipation = (playerStats) => {
        let attended = 0;
        let total = 0;
        filters.forEach(f => {
            if (playerStats.counts[f]) {
                attended += playerStats.counts[f].attended;
                total += playerStats.counts[f].total;
            }
        });
        if (total === 0) return null;
        return Math.round((attended / total) * 100);
    };

    const allEvents = useMemo(() => {
        const eventMap = new Map();
        stats.forEach(player => {
            player.recent_events?.forEach(event => {
                if (!eventMap.has(event.termin_id)) {
                    eventMap.set(event.termin_id, event);
                }
            });
        });
        return Array.from(eventMap.values()).sort((a, b) => new Date(a.datum) - new Date(b.datum));
    }, [stats]);

    const filteredEvents = allEvents.filter(event => filters.includes(event.typ));

    const sortedPlayers = stats
        .map(p => ({ ...p, perc: calculateParticipation(p) }))
        .sort((a, b) => (b.perc ?? -1) - (a.perc ?? -1));

    const handleIconClick = (e, attendance) => {
        setPopoverAnchor(e.currentTarget);
        setPopoverContent(attendance);
    };

    const handlePopoverClose = () => {
        setPopoverAnchor(null);
        setPopoverContent(null);
    };

    const renderEventStatus = (player, event) => {
        const attendance = player.recent_events?.find(ev => ev.termin_id === event.termin_id);
        const cfg = variantConfig[attendance?.variant] ?? variantConfig['null'];

        return (
            <TableCell
                key={`${player.user_id}-${event.termin_id}`}
                onClick={(e) => handleIconClick(e, attendance)}
                sx={{
                    width: EVENT_CELL_WIDTH,
                    minWidth: EVENT_CELL_WIDTH,
                    maxWidth: EVENT_CELL_WIDTH,
                    padding: '16px 4px',
                    textAlign: 'center',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        bgcolor: 'rgba(190, 242, 100, 0.05)',
                        transform: 'scale(1.05)',
                        zIndex: 1
                    }
                }}
            >
                <Box sx={{ color: cfg.color, display: 'flex', justifyContent: 'center' }}>
                    {cfg.icon}
                </Box>
            </TableCell>
        );
    };

    return (
        <Box sx={{ p: { xs: 0, md: 0 }, mt: 4, width: '100%' }}>
            <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end', justifyContent: 'space-between', width: '100%' }}>
                <ToggleButtonGroup
                    value={filters}
                    onChange={handleFilterChange}
                    size="small"
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: "12px",
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.5,
                        '& .MuiToggleButton-root': {
                            borderRadius: "12px",
                            border: 'none',
                            px: 3,
                            fontWeight: 900,
                            color: 'text.secondary',
                            '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: '#000',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }
                        }
                    }}
                >
                    <ToggleButton value="Training">Trainings</ToggleButton>
                    <ToggleButton value="Spiel">Spiele</ToggleButton>
                    <ToggleButton value="Event">Events</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Stack direction="row" spacing={3} sx={{ mb: 4, flexWrap: 'wrap', gap: 2 }}>
                {[
                    { color: "green", label: 'Ja' },
                    { color: theme.palette.error.main, label: 'Nein' },
                    { color: theme.palette.warning.main, label: 'Vielleicht' },
                    { color: theme.palette.text.disabled, label: 'Nichts' },
                ].map(l => (
                    <Stack key={l.label} direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: l.color, boxShadow: `0 0 10px ${l.color}` }} />
                        <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.7rem', opacity: 0.8 }}>
                            {l.label}
                        </Typography>
                    </Stack>
                ))}
            </Stack>

            {filteredEvents.length > 0 ? (
                <TableContainer
                    component={Paper}
                    sx={{
                        borderRadius: "12px",
                        border: '1px solid',
                        borderColor: 'divider',
                        background: 'rgba(255,255,255,0.01)',
                        overflowX: 'auto', // ← FIX: war overflow: 'hidden'
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(190, 242, 100, 0.3)', borderRadius: "12px" }
                    }}
                >
                    <Table stickyHeader size="small" sx={{ minWidth: PLAYER_COLUMN_WIDTH + filteredEvents.length * EVENT_CELL_WIDTH, tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                        bgcolor: 'background.paper', // ← FIX: war '#ffffff'
                                        borderRight: '2px solid',
                                        borderColor: 'divider',
                                        width: PLAYER_COLUMN_WIDTH,
                                        fontWeight: 900,
                                        color: 'primary.main',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.1em',
                                        py: 3
                                    }}
                                >
                                    SPIELER PROFIL
                                </TableCell>
                                {filteredEvents.map(event => (
                                    <TableCell
                                        key={event.termin_id}
                                        sx={{
                                            width: EVENT_CELL_WIDTH,
                                            textAlign: 'center',
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper', // ← FIX: war '#ffffff'
                                            py: 2
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 900, display: 'block', color: 'text.primary' }}>
                                            {new Date(event.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', opacity: 0.8 }}>
                                            {event.typ}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPlayers.map(player => (
                                <TableRow key={player.user_id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                    <TableCell
                                        sx={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            bgcolor: 'background.paper', // ← FIX: war '#ffffff'
                                            borderRight: '2px solid',
                                            borderColor: 'divider',
                                            width: PLAYER_COLUMN_WIDTH,
                                            py: 2.5
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {player.first_name} {player.last_name.charAt(0)}.
                                        </Typography>
                                        <Typography sx={{ fontFamily: '"Fira Code", monospace', fontSize: '0.7rem', fontWeight: 900, color: (player.perc || 0) >= 80 ? 'primary.main' : 'text.secondary' }}>
                                            {player.perc !== null ? `${player.perc}%` : '–'}
                                        </Typography>
                                    </TableCell>
                                    {filteredEvents.map(event => renderEventStatus(player, event))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ textAlign: 'center', py: 10, border: '1px dashed', borderColor: 'divider', borderRadius: "12px" }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        Keine Daten für diesen Zeitraum verfügbar.
                    </Typography>
                </Box>
            )}

            <Popover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "12px",
                            p: 3,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                            maxWidth: 300,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            background: 'rgba(20, 20, 20, 0.95)',
                            backdropFilter: 'blur(15px)'
                        }
                    }
                }}
            >
                {popoverContent && (
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                            <Box sx={{ color: variantConfig[popoverContent?.variant]?.color }}>
                                {variantConfig[popoverContent?.variant]?.icon}
                            </Box>
                            <Typography variant="subtitle1" fontWeight="900" sx={{ color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                {variantConfig[popoverContent?.variant]?.label || 'Offen'}
                            </Typography>
                        </Stack>
                        {popoverContent?.notiz ? (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary', borderColor: 'primary.main', p: 1, bgcolor: 'rgb(219, 219, 219)', borderRadius: '8px' }}>
                                „{popoverContent.notiz}"
                            </Typography>
                        ) : (
                            null
                        )}
                    </Box>
                )}
            </Popover>
        </Box>
    );
};

export default AttendanceStats;